import {
  BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT,
  BOT_SEQUENCE_STEP_LIMITS,
  BotSequenceConfigStep,
  BotSequenceConfigStepDelivery,
  BotSequenceDeliveryChannel,
  BotSequenceType,
  Channel,
  defaultCopyFor
} from "@swivl-mono/types";
import { Select, SelectSize, Switch, Tooltip, VariableTypeahead } from "@swivl-mono/ui";

import { useSequencesV3Context } from "../SequencesV3.Context";
import { SEQUENCE_TYPE_META } from "../sequenceTypeMeta";
import { DeliveryEditor } from "./DeliveryEditor";
import { useSequenceVariables } from "./LocaleBodiesEditor";

export interface DayCapabilityNotes {
  /** Why the FMS note cannot be turned on here; null = allowed. */
  fmsNoteDisabledReason: string | null;
  /** Extra context when the note applies to only some locations in the scope. */
  fmsNoteInfo: string | null;
  /** Why email cannot be turned on here (no Mandrill App for the org); null = allowed. */
  emailDisabledReason: string | null;
}

interface DaySettingsProps {
  sequenceType: BotSequenceType;
  step: BotSequenceConfigStep;
  disabled: boolean;
  capability: DayCapabilityNotes;
  onChange: (patch: Partial<BotSequenceConfigStep>) => void;
}

interface SettingRowProps {
  title: string;
  caption: string;
  tooltip?: string;
  on: boolean;
  disabled: boolean;
  onSet: (on: boolean) => void;
  children?: React.ReactNode;
}

function SettingRow({ title, caption, tooltip, on, disabled, onSet, children }: SettingRowProps): React.ReactElement {
  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="body-sm-alt flex items-center gap-1.5 text-grey-900">
            {title}
            {tooltip && (
              <Tooltip content={tooltip}>
                <span
                  className="body-xs inline-flex size-4 items-center justify-center rounded-full border border-grey-400 text-grey-600"
                  aria-label="More information"
                >
                  ?
                </span>
              </Tooltip>
            )}
          </div>
          <div className="body-xs text-grey-600">{caption}</div>
        </div>
        <Switch on={on} onSet={onSet} isDisabled={disabled} activeColor="orange" />
      </div>
      {on && children && <div className="flex flex-col gap-3 border-t border-grey-100 px-4 py-3">{children}</div>}
    </section>
  );
}

const ALWAYS_SEND_TOOLTIP =
  "Sends on this exact day even on weekends, holidays or when Sequence Sending Hours are closed, and ignores the minimum gap and agent suppression. Quiet hours are never broken: if the day's legal window has already closed, it goes out at the next legal opening and a ticket is opened for this location so staff can follow up.";

const SEND_IMMEDIATELY_TOOLTIP = "Goes out at the first allowed moment on this day instead of a spread-out time.";

const NO_STARTING_SCRIPT = "";

/**
 * Starting script (ENG-3403): the call script the bot begins with when the customer replies to
 * this day's message. The bot's other scripts still apply if the customer takes the conversation
 * elsewhere; a script marked "for sequences only" appears only this way.
 */
function StartingScriptRow({
  value,
  disabled,
  onSet
}: {
  value: string | undefined;
  disabled: boolean;
  onSet: (slug: string | undefined) => void;
}): React.ReactElement {
  const { scripts, loading } = useSequencesV3Context().scripts;
  const options = [{ label: "No starting script", value: NO_STARTING_SCRIPT }, ...scripts.map((s) => ({ label: s.name, value: s.slug }))];
  const current = value && scripts.some((s) => s.slug === value) ? value : NO_STARTING_SCRIPT;
  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="body-sm-alt text-grey-900">Starting script</div>
          <div className="body-xs text-grey-500">
            When the customer replies to this day&apos;s message, the bot begins with this call script. Its other scripts still apply if the
            conversation goes elsewhere.
            {value && current === NO_STARTING_SCRIPT && !loading && <span className="text-red-600"> The script set here is no longer in force.</span>}
          </div>
        </div>
        <div className="w-64 shrink-0">
          <Select
            size={SelectSize.MEDIUM}
            hideClear
            disabled={disabled || loading}
            selectedValues={[current]}
            options={options}
            onChange={(values) => {
              const next = values[0] ?? NO_STARTING_SCRIPT;
              onSet(next === NO_STARTING_SCRIPT ? undefined : next);
            }}
          />
        </div>
      </div>
    </section>
  );
}

/** Everything one day does: SMS, voice call, email, FMS note, and the two day settings. All settings, not actions (decision 33). */
export function DaySettings({ sequenceType, step, disabled, capability, onChange }: DaySettingsProps): React.ReactElement {
  const variables = useSequenceVariables();
  const deliveries: BotSequenceConfigStepDelivery[] = step.deliveries ?? [{ channel: Channel.SMS, content: { mode: "generated" } }];
  const deliveryFor = (channel: BotSequenceDeliveryChannel): BotSequenceConfigStepDelivery | undefined =>
    deliveries.find((delivery) => delivery.channel === channel);

  const handleDelivery = (channel: BotSequenceDeliveryChannel, next: BotSequenceConfigStepDelivery | undefined): void => {
    const others = deliveries.filter((delivery) => delivery.channel !== channel);
    const merged = next ? [...others, next] : others;
    // Keep SMS before Voice for a stable display order.
    merged.sort((left, right) => (left.channel === Channel.SMS ? -1 : right.channel === Channel.SMS ? 1 : 0));
    onChange({ deliveries: merged });
  };

  // Switched on with nothing written yet, the email starts as the default subject and body (decision 53).
  const handleEmailToggle = (enabled: boolean): void => {
    const defaults = defaultCopyFor(sequenceType, step.offsetDays);
    const subject = step.email?.subject?.trim() ? step.email.subject : defaults.emailSubject;
    const body = step.email?.body?.trim() ? step.email.body : defaults.emailBody;
    onChange({ email: { enabled, subject, body } });
  };

  const handleFmsNoteToggle = (enabled: boolean): void => {
    const text = step.fmsNote?.text && step.fmsNote.text.trim().length > 0 ? step.fmsNote.text : BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT;
    onChange({ fmsNote: { enabled, text } });
  };

  return (
    <div className="flex flex-col gap-3">
      <DeliveryEditor
        channel={Channel.SMS}
        delivery={deliveryFor(Channel.SMS)}
        sequenceType={sequenceType}
        offsetDays={step.offsetDays}
        disabled={disabled}
        onChange={(next) => handleDelivery(Channel.SMS, next)}
      />
      <DeliveryEditor
        channel={Channel.VOICE}
        delivery={deliveryFor(Channel.VOICE)}
        sequenceType={sequenceType}
        offsetDays={step.offsetDays}
        disabled={disabled}
        onChange={(next) => handleDelivery(Channel.VOICE, next)}
      />

      <SettingRow
        title="Email"
        caption={capability.emailDisabledReason ?? "A one-way email to the tenant, sent from this organization's Mandrill account."}
        on={step.email?.enabled === true && capability.emailDisabledReason === null}
        disabled={disabled || capability.emailDisabledReason !== null}
        onSet={handleEmailToggle}
      >
        <VariableTypeahead
          label="Subject"
          value={step.email?.subject ?? ""}
          onChange={(subject) => onChange({ email: { enabled: true, subject, body: step.email?.body ?? "" } })}
          variables={variables}
          multiline={false}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.emailSubject}
          disabled={disabled}
        />
        <VariableTypeahead
          label="Body"
          value={step.email?.body ?? ""}
          onChange={(body) => onChange({ email: { enabled: true, subject: step.email?.subject ?? "", body } })}
          variables={variables}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.emailBody}
          disabled={disabled}
          placeholder="Type {{ to insert a variable"
        />
      </SettingRow>

      <SettingRow
        title="Note in the FMS"
        caption={capability.fmsNoteDisabledReason ?? capability.fmsNoteInfo ?? "Written on the tenant in the FMS once this day has gone out."}
        on={step.fmsNote?.enabled === true && capability.fmsNoteDisabledReason === null}
        disabled={disabled || capability.fmsNoteDisabledReason !== null}
        onSet={handleFmsNoteToggle}
      >
        <VariableTypeahead
          value={step.fmsNote?.text ?? BOT_SEQUENCE_FMS_NOTE_DEFAULT_TEXT}
          onChange={(text) => onChange({ fmsNote: { enabled: true, text } })}
          variables={variables}
          maxLength={BOT_SEQUENCE_STEP_LIMITS.fmsNoteText}
          disabled={disabled}
          helperText="Type {{ to insert a variable. A variable with no value is written as (unavailable)."
        />
      </SettingRow>

      {SEQUENCE_TYPE_META[sequenceType].offersSendImmediately && (
        <SettingRow
          title="Send immediately"
          caption="Only offered on Move In and Move Out."
          tooltip={SEND_IMMEDIATELY_TOOLTIP}
          on={step.sendASAP === true}
          disabled={disabled}
          onSet={(sendASAP) => onChange({ sendASAP })}
        />
      )}

      <StartingScriptRow value={step.callScriptSlug} disabled={disabled} onSet={(callScriptSlug) => onChange({ callScriptSlug })} />

      <SettingRow
        title="Always send on this day"
        caption="Goes out on this exact date regardless of weekends, holidays, or send-hour closures."
        tooltip={ALWAYS_SEND_TOOLTIP}
        on={step.deferrable === false}
        disabled={disabled}
        onSet={(always) => onChange({ deferrable: !always })}
      />
    </div>
  );
}
